import React, { useState, useEffect } from 'react';
import { normalizeArray } from './utils/normalize';


const TechResumePreview = ({
  componentRef,
  resumeData = {}
}) => {
  // Process arrays with highlighting

  
  // Extract data from resumeData
  const {
    full_name,
    job_title,
    email,
    phone,
    location,
    linkedin,
    github,
    hackerrank,
    leetcode,
    professional_summary,
    profile_image_url,
    work_experience,
    relevant_experience,
    education,
    projects,
    skills = {},
    key_skills = [],
    technical_skills,
    structured_skills,
    soft_skills,
    languages,
    interests,
    publications,
    volunteer_work,
    additional_info,
    achievements,
    certifications
  } = resumeData || {};

  const {
    programming_languages = [],
    frameworks = [],
    tools = [],
    ai_ml_technologies = []
  } = skills || {};

  const isTemplateResume = resumeData?.isTemplate !== false;
  const trimText = (value) => (typeof value === 'string' ? value.trim() : '');
  const pickDisplay = (value, fallback = '') => {
    const trimmed = trimText(value);
    if (trimmed) return trimmed;
    return isTemplateResume ? fallback : '';
  };

  const displayFullName = pickDisplay(full_name, 'Benjamin Anderson');
  const displayJobTitle = pickDisplay(job_title, 'High School Computer Science Teacher');
  const defaultSummaryText = 'Experienced computer science teacher with strong technical skills and proven track record in software development. Excellent problem-solving abilities and attention to detail. Strong communication and teamwork skills. Adaptable and quick learner with passion for continuous improvement.';
  const summaryContent = pickDisplay(professional_summary, defaultSummaryText);
  const shouldRenderSummary = Boolean(summaryContent);
  const contactEmail = pickDisplay(email, 'support@resumedesign.ai');
  const contactPhone = pickDisplay(phone, '(555) 345-6789');
  const contactLocation = pickDisplay(location, 'New York, US');
  const personalWebsite = trimText(resumeData.personal_website);
  const portfolioLink = trimText(resumeData.portfolio);
  const additionalWebsites = Array.isArray(resumeData.websites) ? resumeData.websites.map(trimText).filter(Boolean) : [];

  const experiences =
    (Array.isArray(relevant_experience) && relevant_experience.length > 0
      ? relevant_experience
      : Array.isArray(work_experience)
        ? work_experience
        : []);

  const cleanArray = (value) => normalizeArray(value).map(trimText).filter(Boolean);
  const getExperiencePoints = (exp = {}) => {
    const sources = [
      exp.description_points,
      exp.duties,
      exp.responsibilities,
      exp.summary,
      exp.description,
    ];
    for (const source of sources) {
      const normalized = cleanArray(source);
      if (normalized.length > 0) return normalized;
    }
    return [];
  };

  const formatDateRange = (exp = {}) => {
    const start = trimText(exp.start_date || exp.start_year || '');
    const end = trimText(exp.end_date || exp.end_year || '');
    if (start && end) return `${start} - ${end}`;
    return start || end || '';
  };

  const educationList = Array.isArray(education) ? education : [];
  const projectsList = Array.isArray(projects) ? projects : [];
  const achievementsList = Array.isArray(achievements) ? achievements.map(trimText).filter(Boolean) : [];
  const certificationsList = Array.isArray(certifications) ? certifications : [];
  const interestsList = Array.isArray(interests) ? interests.map(trimText).filter(Boolean) : [];
  const publicationsList = Array.isArray(publications) ? publications.map(trimText).filter(Boolean) : [];
  const volunteerList = Array.isArray(volunteer_work) ? volunteer_work.map(trimText).filter(Boolean) : [];
  const additionalInfoList = Array.isArray(additional_info) ? additional_info.map(trimText).filter(Boolean) : [];

  // Handle both template and uploaded resume data
  const displaySkills = key_skills.length > 0 || 
    (programming_languages.length > 0 || frameworks.length > 0 || tools.length > 0 || ai_ml_technologies.length > 0);

  // Natural flow - no complex overflow logic needed



  // Determine which skills to display
  const displaySkillsLegacy = structured_skills || skills || { 
    programming_languages: technical_skills || [],
    frameworks: [],
    tools: [],
    ai_ml_technologies: []
  };

  const renderSkillLevel = (level = 4) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={`star ${i < level ? 'filled' : ''}`}>
          ●
        </span>
      );
    }
    return <div className="skill-level">{stars}</div>;
  };

     const renderSkillBar = (skill, level = 9) => {
     const segments = 5;
     const filledSegments = Math.ceil((level / 10) * segments);
     
     return (
       <div className="skill-bar-item">
         <div className="skill-bar-label">{skill}</div>
         <div className="skill-bar-container">
           {Array.from({ length: segments }, (_, index) => (
             <div 
               key={index} 
               className={`skill-segment ${index < filledSegments ? 'filled' : ''}`}
             ></div>
           ))}
         </div>
       </div>
     );
   };

  return (
    <div id="section-to-print">
      {/* PAGE 1 */}
    <div className="tech-resume-preview" ref={componentRef}>
      <div className="tech-resume-container">
        {/* Left Sidebar - Blue */}
        <div className="tech-sidebar">
          {/* Profile Image */}
          <div className="tech-profile-section">
            {profile_image_url ? (
              <div className="tech-profile-image">
                <img src={profile_image_url} alt="Profile" />
              </div>
            ) : (
              <div className="tech-profile-placeholder">
                <div className="placeholder-circle">
                  <span>👤</span>
                </div>
              </div>
            )}
          </div>

          {/* Contact Details */}
          <div className="tech-section">
            <h3 className="tech-section-title">Contact Details</h3>
            {[
              { icon: '✉️', text: contactEmail, href: trimText(email) },
              { icon: '📞', text: contactPhone, href: trimText(phone) ? `tel:${trimText(phone)}` : '' },
              { icon: '🏠', text: contactLocation },
              ...(personalWebsite
                ? [{ icon: '🌐', text: personalWebsite, href: personalWebsite }]
                : []),
              ...(portfolioLink
                ? [{ icon: '🗂️', text: portfolioLink, href: portfolioLink }]
                : []),
              ...additionalWebsites.map((site) => ({ icon: '🔗', text: site, href: site })),
            ]
              .filter((item) => trimText(item.text))
              .map((item, idx) => (
                <div className="tech-contact-item" key={`contact-${idx}`}>
                  <span className="contact-icon">{item.icon}</span>
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-text"
                    >
                      {item.text}
                    </a>
                  ) : (
                    <span className="contact-text">{item.text}</span>
                  )}
                </div>
              ))}
            {[
              { icon: '💼', label: 'LinkedIn', href: trimText(linkedin) },
              { icon: '🔗', label: 'GitHub', href: trimText(github) },
              { icon: '🧠', label: 'HackerRank', href: trimText(hackerrank) },
              { icon: '🧩', label: 'LeetCode', href: trimText(leetcode) },
            ]
              .filter((item) => item.href)
              .map((item, idx) => (
                <div className="tech-contact-item" key={`social-${idx}`}>
                  <span className="contact-icon">{item.icon}</span>
                  <a href={item.href} target="_blank" rel="noopener noreferrer" className="contact-text">
                    {item.label}
                  </a>
                </div>
              ))}
          </div>

                                {/* Technical Skills with Bars */}
            <div className="tech-section">
              <h3 className="tech-section-title">Skills</h3>
              <div className="tech-skills-bars">
                {(() => {
                  const allSkills = [
                    ...(programming_languages || []).map(skill => ({ skill, level: 9 })),
                    ...(frameworks || []).map(skill => ({ skill, level: 8 })),
                    ...(tools || []).map(skill => ({ skill, level: 8 })),
                    ...(ai_ml_technologies || []).map(skill => ({ skill, level: 7 }))
                  ];
                  
                  // Fallback to key_skills if structured skills are empty
                  const fallbackSkills = allSkills.length === 0 && key_skills && key_skills.length > 0 
                    ? key_skills.map(skill => ({ skill, level: 8 }))
                    : [];
                  
                  const finalSkills = allSkills.length > 0 ? allSkills : fallbackSkills;
                  
                  if (finalSkills.length > 0) {
                    return finalSkills.map(({ skill, level }, index) => renderSkillBar(skill, level + (index % 2)));
                  } else if (isTemplateResume) {
                    return [
                  { skill: "Java", level: 9 },
                  { skill: "Python", level: 9 },
                  { skill: "C++", level: 8 },
                  { skill: "SQL", level: 8 },
                  { skill: "JavaScript", level: 8 },
                  { skill: "Algorithms", level: 8 },
                  { skill: "Data Structures", level: 8 },
                  { skill: "Cybersecurity", level: 7 },
                  { skill: "Networking", level: 7 },
                  { skill: "Web Development", level: 7 },
                  { skill: "Database Management", level: 7 },
                  { skill: "Software Engineering", level: 7 }
                    ].map(({ skill, level }) => renderSkillBar(skill, level));
                  }
                  return null;
                })()}
              </div>
            </div>

            {/* Divider after Skills */}
            <div style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#e0e0e0',
              margin: '20px 0',
              opacity: 0.6
            }}></div>

                                {/* Soft Skills with Dots */}
            {/* Removed - only show if user has soft skills data */}

          {/* Languages */}
          {languages && languages.length > 0 && (
            <div className="tech-sidebar-section">
              <h3 className="tech-sidebar-title">Languages</h3>
              <div className="tech-languages-list">
                {languages.map((lang, index) => {
                  // Handle both string and object formats
                  if (typeof lang === 'string') {
                    return (
                      <div key={index} className="tech-language-item">
                        {lang}
                      </div>
                    );
                  }
                  // Object format: extract language/name and proficiency
                  const languageName = lang.language || lang.name || '';
                  const proficiencyLevel = lang.proficiency || '';
                  return (
                    <div key={index} className="tech-language-item">
                      {languageName}{proficiencyLevel ? ` (${proficiencyLevel})` : ''}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          
        </div>

        {/* Right Content - White */}
        <div className="tech-content">
          <div className="tech-header">
            {displayFullName && <h1 className="tech-name">{displayFullName}</h1>}
            {displayJobTitle && <p className="tech-title">{displayJobTitle}</p>}
          </div>

          {shouldRenderSummary && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Professional Summary</h3>
              <p className="tech-summary">{summaryContent}</p>
            </div>
          )}

          {/* Work Experience */}
          {experiences.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Work Experience</h3>
              <div className="tech-experience-list">
                {experiences.map((exp, index) => {
                  const bullets = getExperiencePoints(exp);
                  const dateRange = formatDateRange(exp);
                  const companyName = trimText(exp.company || exp.employer || '');
                  const titleText = trimText(exp.position || exp.title || '');
                  const locationText = trimText(exp.location);
                  return (
                    <div key={index} className="tech-experience-item">
                      <div className="tech-experience-header">
                        {titleText && <h4 className="tech-experience-title">{titleText}</h4>}
                        {companyName && <div className="tech-experience-company">{companyName}</div>}
                        {(locationText || dateRange) && (
                          <div className="tech-experience-duration">
                            {[locationText, dateRange].filter(Boolean).join(' • ')}
                          </div>
                        )}
                      </div>
                      {bullets.length > 0 && (
                        <ul className="tech-exp-responsibilities">
                          {bullets.map((point, idx) => (
                            <li key={idx}>{point}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

            {/* Projects - Never show on first page, always goes to 2nd page */}

          {/* Education */}
          {educationList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Education</h3>
              <div className="tech-education-list">
                {educationList.map((edu, index) => {
                  const degreeText = trimText(edu.degree || edu.qualification || '');
                  const institutionText = trimText(edu.institute || edu.institution || edu.school || '');
                  const fieldText = trimText(edu.field_of_study);
                  const dateRange = formatDateRange(edu);
                  const locationText = trimText(edu.location);
                  const coursework = cleanArray(edu.coursework);
                  const descriptionText = trimText(edu.description);
                  const cgpaText = trimText(edu.cgpa || edu.gpa);
                  return (
                    <div key={index} className="tech-education-item">
                      <div className="tech-education-header">
                        {degreeText && <h4 className="tech-education-degree">{degreeText}</h4>}
                        {institutionText && <div className="tech-education-institution">{institutionText}</div>}
                        {(fieldText || locationText) && (
                          <div className="tech-education-field">
                            {[fieldText, locationText].filter(Boolean).join(' • ')}
                          </div>
                        )}
                        {dateRange && <div className="tech-education-duration">{dateRange}</div>}
                        {cgpaText && <div className="tech-education-cgpa">CGPA: {cgpaText}</div>}
                      </div>
                      {coursework.length > 0 && (
                        <p className="tech-edu-description">
                          <strong>Relevant Coursework:</strong> {coursework.join(', ')}
                        </p>
                      )}
                      {descriptionText && <p className="tech-edu-description">{descriptionText}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Projects - ALWAYS after education */}
          {projectsList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Projects</h3>
              <div className="tech-projects-list">
                {projectsList.map((project, index) => {
                  const titleText = trimText(project.title || project.name || '');
                  const yearText = trimText(project.year);
                  const linkText = trimText(project.link || project.github_link);
                  const descriptionItems = Array.isArray(project.description)
                    ? project.description.map(trimText).filter(Boolean)
                    : cleanArray(project.description);
                  let technologyItems = Array.isArray(project.technologies)
                    ? project.technologies.map(trimText).filter(Boolean)
                    : [];
                  if (technologyItems.length === 0) {
                    const techStack = project.tech_stack;
                    if (Array.isArray(techStack)) {
                      technologyItems = techStack.map(trimText).filter(Boolean);
                    } else if (typeof techStack === 'string') {
                      technologyItems = cleanArray(techStack);
                    }
                  }
                  return (
                    <div key={index} className="tech-project-item">
                      <div className="tech-project-header">
                        {titleText && <h4 className="tech-project-title">{titleText}</h4>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {yearText && (
                            <span
                              style={{
                                fontSize: '0.875rem',
                                color: '#666',
                                fontStyle: 'italic',
                              }}
                            >
                              {yearText}
                            </span>
                          )}
                          {linkText && (
                            <a
                              href={linkText}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tech-project-link"
                            >
                              View Project
                            </a>
                          )}
                        </div>
                      </div>
                      {descriptionItems.length > 0 && (
                        <ul className="tech-project-description">
                          {descriptionItems.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      )}
                      {technologyItems.length > 0 && (
                        <div className="tech-project-tech">
                          Technologies: {technologyItems.join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {achievementsList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Achievements</h3>
              <ul className="tech-achievements-list">
                {achievementsList.map((achievement, index) => (
                  <li key={index}>{achievement}</li>
                ))}
              </ul>
            </div>
          )}

          {certificationsList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Certifications</h3>
              <div className="tech-certifications-list">
                {certificationsList.map((cert, index) => {
                  const isObject = typeof cert === 'object' && cert !== null;
                  const name = isObject ? trimText(cert.name || cert.title || '') : trimText(cert);
                  const provider = isObject ? trimText(cert.provider) : '';
                  const date = isObject ? trimText(cert.date || cert.year || cert.issued_on || cert.issued) : '';
                  if (!name) {
                    return null;
                  }
                  return (
                    <div key={index} className="tech-certification-item">
                      <div className="tech-cert-name">{name}</div>
                      {provider && <div className="tech-cert-provider">{provider}</div>}
                      {date && <div className="tech-cert-date">{date}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {interestsList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Interests</h3>
              <div className="tech-edu-description">{interestsList.join(' • ')}</div>
            </div>
          )}

          {publicationsList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Publications</h3>
              <ul className="tech-achievements-list">
                {publicationsList.map((publication, index) => (
                  <li key={index}>{publication}</li>
                ))}
              </ul>
            </div>
          )}

          {volunteerList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Volunteer Work</h3>
              <ul className="tech-achievements-list">
                {volunteerList.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {additionalInfoList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Additional Information</h3>
              <ul className="tech-achievements-list">
                {additionalInfoList.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
            </div>
          </div>
        </div>



      <style jsx>{`
                 .tech-resume-preview {
           width: 100%;
           max-width: 794px;
           min-height: auto; /* Remove fixed height - let content determine height */
           margin: 0 auto;
           background: white;
           box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
           font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
           overflow: visible; /* Allow content to grow naturally */
           position: relative;
           display: flex;
         }

                 .tech-resume-container {
           display: flex;
           width: 100%;
          min-height: 100%;
           padding: 0;
           margin: 0;
          overflow: visible; /* Allow natural growth */
          flex-direction: row;
          align-items: stretch; /* Ensure both columns grow to same height */
         }

                 .tech-sidebar {
           width: 35%;
           background: #3b82f6 100%;
           color: white;
           padding: 20px 20px;
           box-sizing: border-box;
          min-height: fit-content; /* Remove fixed height - grow with content */
          overflow: visible; /* Allow natural growth */
          display: flex;
          flex-direction: column;
         }

                 .tech-content {
           width: 65%;
           background: white;
           padding: 30px 30px;
           box-sizing: border-box;
          min-height: fit-content; /* Remove fixed height - grow with content */
          overflow: visible; /* Allow natural growth */
          display: flex;
          flex-direction: column;
         }

                 .tech-profile-section {
           text-align: center;
           margin-bottom: 20px;
         }

        .tech-profile-image img {
          width: 90px;
          height: 90px;
          border-radius: 50%;
          border: 4px solid white;
          object-fit: cover;
        }

        .tech-profile-placeholder {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 90px;
          height: 90px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.2);
          margin: 0 auto;
        }

        .placeholder-circle {
          width: 70px;
          height: 70px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
        }

                 .tech-section {
           
         }

                 .tech-section-title {
           font-size: 16px;
           font-weight: bold;
           margin-bottom: 12px;
           color: white;
           border-bottom: 2px solid rgba(255, 255, 255, 0.3);
           padding-bottom: 6px;
           text-transform: uppercase;
           letter-spacing: 0.5px;
         }

                 .tech-contact-item {
           display: flex;
           align-items: center;
           margin-bottom: 10px;
           font-size: 12px;
         }

        .contact-icon {
          margin-right: 12px;
          font-size: 16px;
          width: 20px;
        }

        .contact-text {
          color: rgba(255, 255, 255, 0.9);
          word-break: break-all;
        }

        .tech-skills-list {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .tech-skill-category {
          margin-bottom: 15px;
        }

        .tech-skill-category-title {
          font-size: 14px;
          font-weight: bold;
          margin-bottom: 10px;
          color: rgba(255, 255, 255, 0.9);
        }

        .tech-skill-items {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .tech-skill-tag {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          padding: 5px 10px;
          border-radius: 15px;
          font-size: 11px;
          font-weight: 500;
        }

                 .tech-skills-bars {
           display: flex;
           flex-direction: column;
           gap: 4px;
         }

                 .skill-bar-item {
           display: flex;
           flex-direction: column;
           margin-bottom: 6px;
         }

                 .skill-bar-label {
           color: rgba(255, 255, 255, 0.9);
           font-weight: normal;
           font-size: 10px;
           margin-bottom: 3px;
         }

                 .skill-bar-container {
           display: flex;
           gap: 1px;
           height: 10px;
           background: transparent;
         }

                 .skill-segment {
           flex: 1;
           height: 50%;
           background: rgba(255, 255, 255, 0.3);
           border-radius: 0;
           position: relative;
           clip-path: polygon(4px 0, 100% 0, calc(100% - 4px) 100%, 0 100%);
         }
         
         .skill-segment.filled {
           background: white;
         }
         
         .skill-segment.filled:last-child {
           background: rgba(255, 255, 255, 0.7);
         }

        

                 .soft-skills-list {
           display: flex;
           flex-direction: column;
           
         }

                 .soft-skill-item {
           display: flex;
           justify-content: space-between;
           align-items: center;
           font-size: 12px;
           margin-bottom: 8px;
         }

        .soft-skill-name {
          color: rgba(255, 255, 255, 0.9);
        }

                 .skill-level {
           display: flex;
           gap: 4px;
         }

                 .star {
           font-size: 27px;
           color: rgba(255, 255, 255, 0.3);
         }

                 .star.filled {
           color: white;
         }

        .languages-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .language-tag {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          padding: 5px 12px;
          border-radius: 15px;
          font-size: 12px;
        }

        

        .tech-header {
          margin-bottom: 25px;
          text-align: left;
        }

        .tech-name {
          font-size: 32px;
          font-weight: bold;
          color: #1e3a8a;
          margin: 0 0 8px 0;
          line-height: 1.2;
        }

        .tech-title {
          font-size: 20px;
          color: #64748b;
          margin: 0 0 15px 0;
          font-weight: 600;
        }

        .tech-summary {
          font-size: 14px;
          line-height: 1.6;
          color: #374151;
          margin: 0;
          text-align: left;
        }

        .tech-content-section {
          margin-bottom: 25px;
        }

        .tech-content-title {
          font-size: 20px;
          font-weight: bold;
          color: #1e3a8a;
          margin-bottom: 15px;
          border-bottom: 2px solid #e5e7eb;
          padding-bottom: 5px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .tech-experience-list,
        .tech-projects-list,
        .tech-education-list {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .tech-experience-item,
        .tech-project-item,
        .tech-education-item {
          margin-bottom: 15px;
        }

        .tech-exp-header,
        .tech-edu-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 10px;
        }

        .tech-exp-left,
        .tech-edu-left {
          flex: 1;
        }

        .tech-exp-position,
        .tech-project-title,
        .tech-edu-degree {
          font-size: 16px;
          font-weight: bold;
          color: #1e3a8a;
          margin: 0 0 4px 0;
        }

        .tech-exp-company,
        .tech-edu-school {
          font-size: 14px;
          color: #64748b;
          margin: 0;
          font-weight: 500;
        }

        .tech-exp-dates,
        .tech-edu-dates {
          font-size: 12px;
          color: #6b7280;
          font-weight: 600;
          text-align: right;
          white-space: nowrap;
        }

        .tech-exp-responsibilities {
          list-style: disc;
          margin: 10px 0 0 20px;
          padding: 0;
          list-style-position: outside;
        }

        .tech-exp-responsibilities li {
          font-size: 13px;
          line-height: 1.5;
          color: #374151;
          margin-bottom: 5px;
          padding-left: 0;
          margin-left: 0;
          display: list-item;
          list-style-type: disc;
          list-style-position: outside;
        }

        .tech-edu-description {
          font-size: 13px;
          line-height: 1.5;
          color: #374151;
          margin: 8px 0 0 0;
        }

        .tech-project-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }

        .tech-project-link {
          font-size: 12px;
          color: #3b82f6;
          text-decoration: none;
        }

        .tech-project-description {
          list-style: disc;
          margin: 8px 0 0 20px;
          padding: 0;
        }

        .tech-project-description li {
          font-size: 13px;
          line-height: 1.5;
          color: #374151;
          margin-bottom: 4px;
        }

        .tech-project-tech {
          font-size: 12px;
          color: #64748b;
          margin: 8px 0 0 0;
        }

        .tech-achievements-list {
          list-style: disc;
          padding-left: 20px;
          margin: 0;
        }

        .tech-achievements-list li {
          font-size: 13px;
          line-height: 1.5;
          color: #374151;
          margin-bottom: 4px;
        }

        .tech-certifications-list {
          margin-top: 10px;
        }

        .tech-certification-item {
          margin-bottom: 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
        }

        .tech-certification-item:last-child {
          border-bottom: none;
        }

        .tech-cert-name {
          font-weight: 600;
          color: #1f2937;
          font-size: 11pt;
          margin-bottom: 2px;
        }

        .tech-cert-provider {
          color: #6b7280;
          font-size: 9pt;
          font-style: italic;
        }

        .tech-cert-date {
          color: #6b7280;
          font-size: 9pt;
          margin-top: 2px;
        }

        @media print {
          @page {
            size: A4;
            margin: 0;
            background: white;
          }
          
          html, body {
            background: white !important;
            margin: 0;
            padding: 0;
          }
          
          #section-to-print {
            background: white !important;
          }
          
          .tech-resume-preview {
            box-shadow: none;
            width: var(--a4-print-width) !important;
            max-width: var(--a4-print-width) !important;
            min-height: auto;
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .tech-resume-container {
            background: white !important;
          }
          
          .tech-content {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default TechResumePreview;
